import { FieldPath, useFormContext } from 'react-hook-form';
import { EmploymentType, IncomeVerificationType } from '@brighte/validation';
import { Alert } from '@spark-web/alert';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Heading } from '@spark-web/heading';
import { EyeIcon, InformationCircleIcon, XCircleIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { INCOME_VERIFICATION } from '../../utils';
import { IncomeVerificationState } from './IncomeVerificationOptions';

type BankStatementProps = {
  setCurrentState: (state: IncomeVerificationState) => void;
  incomeType: FieldPath<IncomeVerificationType>;
  financialProductId: string;
};

function BankStatements({ setCurrentState, incomeType }: BankStatementProps) {
  const { watch } = useFormContext<IncomeVerificationType>();

  const {
    BANK_STATEMENT_INFO,
    BANK_STATEMENT_SELF_EMPLOYED_INFO,
    BANK_STATEMENT_PENSIONER_INFO,
    BANK_STATEMENT_PRIVACY,
    BANK_STATEMENT_HEADER_CREDENTIALS,
    BANK_STATEMENT_HEADER_READ_ACCESS,
    BANK_STATEMENT_CONNECT,
    BANK_STATEMENT_DESCRIPTION,
    UPLOAD,
  } = INCOME_VERIFICATION;

  const bankStateInfo = {
    [EmploymentType.EMPLOYED]: BANK_STATEMENT_INFO,
    [EmploymentType.SELF_EMPLOYED]: BANK_STATEMENT_SELF_EMPLOYED_INFO,
    [EmploymentType.PENSIONER]: BANK_STATEMENT_PENSIONER_INFO,
  };

  const employmentType = watch('applicant.employment.type') ?? EmploymentType.EMPLOYED;
  const isPrimaryIncome = incomeType === 'applicant.income.primary';

  return (
    <Stack gap="xxlarge">
      <>
        <Heading level="3">Option 1</Heading>
        <Text>{isPrimaryIncome ? bankStateInfo[employmentType] : BANK_STATEMENT_INFO}</Text>
        <Button onClick={() => setCurrentState(IncomeVerificationState.UPLOAD_DOCUMENTS)}>{UPLOAD}</Button>
        <Divider />
        <Heading level="3">Option 2</Heading>
        <Text>{BANK_STATEMENT_DESCRIPTION}</Text>
        <Stack gap="xlarge">
          <Heading level="4">
            <EyeIcon tone="positive" /> {BANK_STATEMENT_HEADER_READ_ACCESS}
          </Heading>
          <Heading level="4">
            <XCircleIcon tone="positive" /> {BANK_STATEMENT_HEADER_CREDENTIALS}
          </Heading>
        </Stack>
        <Stack gap="large">
          <Button onClick={() => setCurrentState(IncomeVerificationState.BANK_STATEMENT_VERIFICATION)} prominence="low">
            {BANK_STATEMENT_CONNECT}
          </Button>
        </Stack>
      </>
      <Alert tone="caution" icon={InformationCircleIcon}>
        {BANK_STATEMENT_PRIVACY}
      </Alert>
    </Stack>
  );
}

export default BankStatements;
